import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useTenantView } from './TenantViewContext';
import { buildChartPalette, getLineChartColors } from '../lib/chartColors';

const DEFAULT_BRAND = '#1A428A';
const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const { viewingTenant, isViewingTenant } = useTenantView();

  const brandName = isViewingTenant
    ? viewingTenant.name
    : user?.accountName || 'Venue Intelligence';
  const primaryColor =
    (isViewingTenant ? viewingTenant.primaryColor : user?.primaryColor) || DEFAULT_BRAND;
  const logoUrl = (isViewingTenant ? viewingTenant.logoUrl : user?.logoUrl) || null;
  const chartPalette = useMemo(() => buildChartPalette(primaryColor), [primaryColor]);
  const chartLine = useMemo(() => getLineChartColors(primaryColor), [primaryColor]);

  useEffect(() => {
    document.documentElement.style.setProperty('--brand-primary', primaryColor);
    chartPalette.forEach((color, index) => {
      document.documentElement.style.setProperty(`--chart-palette-${index}`, color);
    });
    return () => {
      document.documentElement.style.setProperty('--brand-primary', DEFAULT_BRAND);
      chartPalette.forEach((_, index) => {
        document.documentElement.style.removeProperty(`--chart-palette-${index}`);
      });
    };
  }, [primaryColor, chartPalette]);

  const value = useMemo(
    () => ({
      brandName,
      primaryColor,
      chartPalette,
      chartLine,
      logoUrl,
      isViewingTenant,
    }),
    [brandName, primaryColor, chartPalette, chartLine, logoUrl, isViewingTenant]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
